import React, { useState, useEffect } from 'react';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import { apiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('All');
  const { addToast } = useToast();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/companies');
      setCompanies(res.data || []);
    } catch (err) {
      addToast('Failed to load companies', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    const confirmMessage = status === 'suspended' ? 'suspend' : status === 'rejected' ? 'reject' : 'approve';
    if (!window.confirm(`Are you sure you want to ${confirmMessage} this company?`)) return;
    
    try {
      await apiClient.put(`/admin/companies/${id}/status`, { status });
      addToast(`Company status updated to ${status}`, 'success');
      setCompanies(companies.map(c => c.id === id ? { ...c, status } : c));
    } catch (err) {
      addToast('Failed to update status', 'error');
    }
  };

  const filtered = companies.filter(c => {
    const matchesSearch = (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (c.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'All' || 
                         (filter === 'Active' && c.status === 'active') || 
                         (filter === 'Pending' && c.status === 'pending') || 
                         (filter === 'Rejected' && c.status === 'rejected') || 
                         (filter === 'Suspended' && c.status === 'suspended');
    return matchesSearch && matchesFilter;
  });

  return (
    <div>
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="admin-page-title">Companies</h1>
          <p className="admin-page-sub">Manage employer accounts and verifications.</p>
        </div>
      </div>

      <div className="card-elevated">
        <div className="flex justify-between items-center mb-6">
          <div className="tabs mb-0 border-none flex-wrap">
            {['All', 'Active', 'Pending', 'Rejected', 'Suspended'].map(tab => (
              <button 
                key={tab} 
                className={`tab-btn ${filter === tab ? 'active' : ''}`}
                onClick={() => setFilter(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
          
          <div className="search-bar w-64">
            <span className="search-icon text-sm">🔍</span>
            <input 
              type="text" 
              placeholder="Search companies..." 
              className="text-sm py-2"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No companies found matching your criteria.</div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Company Info</th>
                  <th>Industry</th>
                  <th>Location & Web</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(company => (
                  <tr key={company.id}>
                    <td>
                      <div className="font-semi text-gray-900">{company.name}</div>
                      <div className="text-xs text-gray-500">{company.email}</div>
                      <div className="text-xs text-gray-400 mt-1">HR: {company.hr_name} ({company.hr_contact})</div>
                    </td>
                    <td className="text-sm">{company.industry}</td>
                    <td>
                      <div className="text-sm">{company.location}</div>
                      <a href={company.website} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">{company.website}</a>
                    </td>
                    <td>
                      <Badge variant={
                        company.status === 'active' ? 'success' : 
                        company.status === 'suspended' ? 'danger' : 
                        company.status === 'rejected' ? 'danger' : 'warning'
                      }>
                        {company.status || 'pending'}
                      </Badge>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        {company.status !== 'active' && (
                          <Button size="sm" variant="outline" className="!py-1 !px-2 text-xs" onClick={() => handleStatusChange(company.id, 'active')}>
                            Approve
                          </Button>
                        )}
                        {company.status === 'pending' && (
                          <Button size="sm" variant="ghost" className="!py-1 !px-2 text-xs text-danger" onClick={() => handleStatusChange(company.id, 'rejected')}>
                            Reject
                          </Button>
                        )}
                        {company.status === 'active' && (
                          <Button size="sm" variant="ghost" className="!py-1 !px-2 text-xs text-danger" onClick={() => handleStatusChange(company.id, 'suspended')}>
                            Suspend
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCompanies;